import { Model } from '../../../../api-gen/gen/models/model';
import {
  AI_MODEL_OPTIONS,
  AiModelOption,
} from '../../shared-components/ai-model-selector/ai-model-selector.types';

export interface ModelShowcase {
  /** URL/anchor friendly identifier derived from the model name. */
  slug: string;
  /**
   * Router path that opens the create page pre-selected on this model, e.g.
   * `/create/default` or `/create/character-creator`. Derived from the model
   * value so it matches the `create/:characterRoute` model lookup.
   */
  route: string;
  /** The underlying selector option (name, icon, short description, badges). */
  option: AiModelOption;
  /** Short tagline shown under the model name. */
  tagline: string;
  /** Long-form, SEO friendly description (placeholder copy for now). */
  longDescription: string;
  /** Key strengths rendered as a checklist. */
  highlights: string[];
  /** Short "best for" tags shown as chips. */
  bestFor: string[];
  /**
   * Public share slugs for the example gallery. Each one is loaded via
   * `/prompts/shared/{shareSlug}` (which returns its media) and links to the
   * public `/share/:shareId` page. Define 3-6 per model.
   */
  shareIds: string[];
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Extended, per-model showcase content keyed by the model value. */
const SHOWCASE_DETAILS: Partial<
  Record<
    Model,
    Pick<
      ModelShowcase,
      'tagline' | 'longDescription' | 'highlights' | 'bestFor' | 'shareIds'
    >
  >
> = {
  [Model.Default]: {
    tagline: 'Our flexible all-rounder for any style or scene.',
    longDescription:
      'Creative v2 is our versatile flagship model, built to understand even complex prompts with ease. Whether you want photorealistic portraits, stylised art, or imaginative scenes, it balances creativity and accuracy to deliver consistently stunning results. It is the perfect starting point for new creators and a reliable workhorse for advanced users.',
    highlights: [
      'Excellent prompt understanding across styles',
      'Great balance of realism and creativity',
      'Reliable composition and lighting',
      'Ideal first model for new creators',
    ],
    bestFor: ['Portraits', 'Creative scenes', 'General use', 'Beginners'],
    shareIds: ['Dkjez', 'NQVyG', 'YAVBaL', 'yD6oN', 'm4yaZ', 'b7a4WD'],
  },
  [Model.Realism]: {
    tagline: 'Photorealistic detail down to the last texture.',
    longDescription:
      'Realism is engineered for lifelike output, producing images with natural skin tones, accurate lighting, and incredibly detailed textures. From close-up portraits to full-body scenes, it captures the subtle imperfections that make an image feel truly real. If your goal is photographic believability, this is the model to reach for.',
    highlights: [
      'Industry-leading photorealism',
      'Natural skin and body detail',
      'Accurate lighting and shadows',
      'Perfect for lifelike portraits',
    ],
    bestFor: ['Photorealism', 'Portraits', 'Editorial', 'Lifelike scenes'],
    shareIds: ['5Ejmd', 'LwWMk', 'lYrXp', 'xwX23', '0oP1l', 'Xdj38m'],
  },
  [Model.Temporary]: {
    tagline: 'A limited-time experimental style to explore.',
    longDescription:
      'Dreamy Realism V2 is a limited-time experimental model that blends soft, dreamlike aesthetics with a realistic foundation. It is the place to explore our newest research and try fresh looks before they graduate into the main lineup. Availability is limited, so it is the perfect model to experiment with something different.',
    highlights: [
      'Cutting-edge experimental style',
      'Soft, dreamy aesthetic',
      'Built on a realistic foundation',
      'Limited-time availability',
    ],
    bestFor: ['Experiments', 'Dreamy looks', 'Early access', 'New styles'],
    shareIds: ['OoEpbP', 'pnkYa', 'o4z3Rw', 'LxpYmV', 'eX1LZW', 'x3wBQ'],
  },
  [Model.CharacterCreator]: {
    tagline: 'Design a consistent character, step by step.',
    longDescription:
      'Character Creator lets you build a character from the ground up by mixing attributes, outfits, scenes, and styles. Instead of crafting a single prompt, you compose your subject piece by piece for full creative control and consistent results across generations. It is ideal for building recurring characters and AI personas.',
    highlights: [
      'Guided, step-by-step character building',
      'Consistent characters across images',
      'Mix attributes, outfits and scenes',
      'Great for recurring AI personas',
    ],
    bestFor: [
      'Consistent characters',
      'AI personas',
      'Storytelling',
      'Control',
    ],
    shareIds: ['r6BQ6g', '8RkyoB', '28VrkW', '62VoO6', 'XQAl8P', '62jj8R'],
  },
  [Model.Anima]: {
    tagline: 'Expressive anime and manga artistry.',
    longDescription:
      'Anima 1.0 is our versatile artistic model focused on anime and manga aesthetics. It excels at clean linework, vibrant colour, and expressive characters while remaining flexible enough for a wide range of illustrated styles. From soft slice-of-life scenes to dynamic action, Anima brings your ideas to life with strong creative range.',
    highlights: [
      'Specialised anime and manga styles',
      'Clean linework and vibrant colour',
      'Expressive character design',
      'Wide artistic range',
    ],
    bestFor: ['Anime', 'Manga', 'Illustration', 'Character art'],
    shareIds: ['WbLg55', '0yN4Vl', 'NLeovd', 'OovWda', 'kjrnPj', 'vQX438'],
  },
  [Model.Zimage]: {
    tagline: 'Authentic, influencer-style social media photos.',
    longDescription:
      'Social Media is tuned to produce high-quality realistic images in an authentic influencer style — the kind of photo that looks like it came straight from a real Instagram feed. Expect natural posing, believable lighting, and a candid feel that makes your AI characters feel genuinely social-ready.',
    highlights: [
      'Authentic Instagram-style results',
      'Natural posing and lighting',
      'Candid, social-ready aesthetic',
      'Perfect for AI influencers',
    ],
    bestFor: ['AI influencers', 'Social media', 'Lifestyle', 'Selfies'],
    shareIds: ['m5VNbk', '6Y73dj', 'BzRYLW', 'g7L2zO', 'OoeYgn', 'Ra1zL2'],
  },
  [Model.Grok]: {
    tagline: 'Advanced realism with sharp prompt understanding.',
    longDescription:
      'Grok Ultra is an advanced model built for realistic images with excellent prompt understanding. It shines at social media and influencer-style content, interpreting nuanced instructions to deliver polished, believable results. When you need precision and quality together, Grok Ultra delivers.',
    highlights: [
      'Advanced prompt understanding',
      'Polished, realistic output',
      'Great for influencer content',
      'Precise and consistent',
    ],
    bestFor: ['Realism', 'Social media', 'Influencer', 'Precision'],
    shareIds: ['Wb2k5r', 'QX4AaW', 'Bz3vvP', 'zX8N6k', '1lkQ7k', 'Lxv5XW'],
  },
  [Model.Seeddance]: {
    tagline: 'High-quality generation with strong prompt control.',
    longDescription:
      'SeedDance 4.5 delivers high-quality image generation with strong prompt understanding. It reliably translates detailed instructions into crisp, well-composed images, making it a dependable choice when you want your prompt followed faithfully without sacrificing visual quality.',
    highlights: [
      'High-quality, crisp output',
      'Strong prompt adherence',
      'Well-composed results',
      'Dependable and consistent',
    ],
    bestFor: ['Detailed prompts', 'Quality', 'Composition', 'Versatility'],
    shareIds: ['nbGRab', 'AdNboR', 'Gz11z7', '3LbZ0Z', '4obxzp', '5dk7Ep'],
  },
  [Model.Krea]: {
    tagline: 'The most aesthetic open model — creative, sexy and uncensored.',
    longDescription:
      'Krea 2 is an aesthetic-first model, bringing polished, gallery-worthy images with an open, malleable foundation. It blends striking creativity with convincing realism, handling everything from editorial portraits to bold, sexy scenes without holding back. Fast and uncensored, Krea 2 gives you the freedom to explore your ideas exactly the way you imagine them.',
    highlights: [
      'Aesthetic-first, gallery-worthy results',
      'Creative range with convincing realism',
      'Fast, polished text-to-image',
      'Uncensored creative freedom',
    ],
    bestFor: ['Aesthetic shots', 'Creative scenes', 'Realism', 'Uncensored'],
    shareIds: ['g7nLp3', '71MBLz', 'AdR8Yl', 'Vzxd4x', 'x5blX6', 'aMopdz'],
  },
  [Model.Anime]: {
    tagline: 'Stylised anime, manga and cartoon characters.',
    longDescription:
      'Anime & Cartoon is a stylised model built for anime, manga, and cartoon-inspired characters and scenes. It leans fully into illustrated aesthetics with bold colour and clean shapes, making it the go-to choice for fans of animated and comic styles who want striking, characterful results.',
    highlights: [
      'Bold anime and cartoon styles',
      'Clean shapes and vibrant colour',
      'Characterful, illustrated look',
      'Great for comic-inspired scenes',
    ],
    bestFor: ['Anime', 'Cartoon', 'Comics', 'Stylised art'],
    shareIds: ['jLZkW', 'r6E1NR', '6Y3Z8b', 'o4O2dM', 'PdeQLY', 'GzlWE4'],
  },
};

/** Build the `create/:characterRoute` segment from a model value. */
const modelRoute = (value: string): string =>
  value.toLowerCase().replace(/_/g, '-');

export const MODEL_SHOWCASES: ModelShowcase[] = AI_MODEL_OPTIONS.map(
  (option) => {
    const slug = slugify(option.name);
    const details = SHOWCASE_DETAILS[option.value as Model];
    return {
      slug,
      route: modelRoute(option.value),
      option,
      tagline: details?.tagline ?? option.description,
      longDescription: details?.longDescription ?? option.description,
      highlights: details?.highlights ?? [],
      bestFor: details?.bestFor ?? [],
      shareIds: details?.shareIds ?? [],
    };
  },
);

export interface ModelFaqItem {
  question: string;
  answer: string;
}

export const MODEL_FAQ: ModelFaqItem[] = [
  {
    question: 'Which AI model should I choose?',
    answer:
      'It depends on your goal. Pick Realism or Grok Ultra for photorealistic images, Social Media for influencer-style photos, Anima or Anime & Cartoon for illustrated styles, and Creative v2 if you want a flexible all-rounder.',
  },
  {
    question: 'Can I switch between AI models?',
    answer:
      'Yes. You can choose a different model every time you generate, so you are free to experiment and find the style that fits your idea best.',
  },
  {
    question: 'Are new AI models added regularly?',
    answer:
      'Absolutely. We continuously train and release new models, and limited-time experimental models let you try the latest styles before anyone else.',
  },
  {
    question: 'Do all models support the same content?',
    answer:
      'Most of our models support a wide range of content, while a few are limited to safe (non-explicit) content. The capabilities of each model are shown on its card above.',
  },
];
